import React, { useCallback, useState } from 'react';

import useFormConfigManager from '../../../hooks/useFormConfigManager/useFormConfigManager';
import type { UnifiedPaymentFormEntryProps } from '../../UnifiedPaymentForm/UnifiedPaymentFormEntry';
import useStartSycurioTelephonicEntry from '../hooks/useStartSycurioTelephonicEntry';
import type { SycurioPCIResponseType } from '../utilities/getSycurioPCIResponse';
import { useSycurioMessageListener } from '../hooks/useSycurioMessageListener';
import useSycurioAgentPageObserver from '../hooks/useSycurioAgentPageObserver';
import useSycurioLogger from '../hooks/useSycurioLogger';
import { TELEPHONIC_ENTRY_ERROR_MESSAGES } from '../constants/messages';
import useTelephonicEntryCleanup from '../hooks/useTelephonicEntryCleanup';
import { useSycurioSessionDetails } from '../hooks/useSycurioSessionDetails';
import type { CheckoutSessionsResponse } from '../../../services/commonCheckout/types/CheckoutSessionsResponse';
import { generateSycurioMessageListenerCallback } from '../utilities/generateSycurioMessageListenerCallback';
import { getSessionId } from '../../../utils/session/selectors';
import { generateFormConfigManagerParameters } from '../utilities/generateFormConfigManagerParameters';
import { getSycurioPageSubTitle } from '../utilities/getSycurioPageSubTitle';
import type { SycurioErrorEventType } from '../types/SycurioTypes';
import { useLanguageDropdownController } from '../hooks/useLanguageDropdownController';

import SycurioTelephonicViewContainer from './SycurioTelephonicViewContainer';

export type SycurioAgentControlPanelProps = Pick<
  UnifiedPaymentFormEntryProps,
  'userJourney' | 'backButtonLabel' | 'onBackClick'
> & {
  onCancel: () => void;
  onDone: (event: {
    sycurioPCIResponse: SycurioPCIResponseType;
  }) => void;
  onError: (errorEvent: SycurioErrorEventType) => void;
  controlSpinner: (event: { isLoaderOn: boolean }) => void;
} & {
  sessionResponse: CheckoutSessionsResponse;
};

const SycurioTelephonicEntry: React.FC<
  SycurioAgentControlPanelProps
> = ({
  userJourney,
  backButtonLabel,
  onBackClick,
  onCancel,
  onDone,
  onError,
  controlSpinner,
  sessionResponse,
}) => {
  const [sycurioPageHeight, setSycurioPageHeight] = useState<
    string | undefined
  >(undefined);

  useLanguageDropdownController();

  const { trackEvent, logError } = useSycurioLogger();

  const checkoutSessionId = getSessionId(sessionResponse);

  const { casiTransactionId } = useStartSycurioTelephonicEntry({
    checkoutSessionId,
    startSpinner: useCallback(() => {
      controlSpinner({ isLoaderOn: true });
    }, []),
  });

  const {
    isSycurioReady,
    sycurioHttpOrigin,
    sycurioFragmentUrl,
    appInsightsMetadata,
    hasSycurioIframeFailedToLoad,
    hasTimeElapseForSycurioInitMessage,
    hasCasiSessionFailed,
  } = useSycurioSessionDetails({
    sessionResponse,
    sycurioPageHeight,
    casiTransactionId,
    checkoutSessionId,
  });

  const { triggerDoneOrDeleteService } =
    useTelephonicEntryCleanup({ checkoutSessionId });

  const onSubmitHandler = useCallback(
    (sycurioPCIResponse: SycurioPCIResponseType) => {
      triggerDoneOrDeleteService({
        shouldCallDoneOrDeleteService: true,
        eventType: 'onDone',
      });
      onDone({ sycurioPCIResponse });
    },
    [],
  );

  const onErrorHandler = useCallback(
    ({ sycurioErrorType, message }: SycurioErrorEventType) => {
      const { sycurioMetadata } = appInsightsMetadata ?? {
        sycurioMetadata: undefined,
      };
      triggerDoneOrDeleteService({
        shouldCallDoneOrDeleteService: [
          'submitError',
          'expiryError',
        ].includes(sycurioErrorType),
        eventType: 'onDelete',
      });
      controlSpinner({ isLoaderOn: false });
      logError({
        error: new Error(
          TELEPHONIC_ENTRY_ERROR_MESSAGES[sycurioErrorType],
        ),
        metadata: {
          code: sycurioErrorType,
          message,
          ...sycurioMetadata,
        },
      });
      onError({
        sycurioErrorType,
        message,
      });
    },
    [appInsightsMetadata.sycurioMetadata.checkoutSessionId],
  );

  const onBackHandler = useCallback(() => {
    triggerDoneOrDeleteService({
      shouldCallDoneOrDeleteService: true,
      eventType: 'onDelete',
    });
    trackEvent(appInsightsMetadata);
    onBackClick?.();
  }, [appInsightsMetadata?.sycurioMetadata.checkoutSessionId]);

  const onCancelHandler = useCallback(() => {
    triggerDoneOrDeleteService({
      shouldCallDoneOrDeleteService: true,
      eventType: 'onDelete',
    });
    if (appInsightsMetadata) {
      trackEvent(appInsightsMetadata);
    }
    onCancel();
  }, [appInsightsMetadata?.sycurioMetadata.checkoutSessionId]);

  useSycurioMessageListener({
    callback: useCallback(
      generateSycurioMessageListenerCallback({
        sycurioHttpOrigin,
        onErrorHandler,
        onSubmitHandler,
        setSycurioPageHeight,
      }),
      [sycurioHttpOrigin, onErrorHandler, onSubmitHandler],
    ),
  });

  const { title, titleTestId, showActionsOnForm } =
    useFormConfigManager(
      generateFormConfigManagerParameters({
        userJourney,
        backButtonLabel,
        onBackHandler,
        onCancelHandler,
        showBackButton: !!onBackClick,
      }),
    );

  useSycurioAgentPageObserver({
    hasCasiSessionFailed,
    hasSycurioIframeFailedToLoad,
    stopSpinner: () => {
      controlSpinner({ isLoaderOn: false });
    },
    onError: onErrorHandler,
    hasTimeElapseForSycurioInitMessage,
    sessionResponse,
  });

  return (
    <SycurioTelephonicViewContainer
      showActionsOnForm={showActionsOnForm}
      backButtonLabel={backButtonLabel}
      onBackClick={onBackClick ? onBackHandler : undefined}
      title={title}
      titleTestId={titleTestId}
      subTitle={getSycurioPageSubTitle(userJourney)}
      sycurioFragmentUrl={sycurioFragmentUrl}
      isSycurioReady={isSycurioReady}
      sycurioPageHeight={sycurioPageHeight}
      onCancelHandler={onCancelHandler}
      userJourney={userJourney}
    />
  );
};

export default SycurioTelephonicEntry;
